<script setup>
import { computed, ref } from 'vue'
import { useI18n } from '../../i18n'
import { permissionAreaName, permissionGroupName, permissionName } from '../../access/labels.js'

const props = defineProps({ groups: { type: Array, required: true }, selected: { type: Array, required: true }, readonly: { type: Boolean, default: true } })
const emit = defineEmits(['update:selected'])
const { t } = useI18n()
const expandedGroups = ref(new Set())
const areaOrder = ['general', 'platform', 'naftapos', 'pts2']
const areas = computed(() => areaOrder.map((key) => ({
  key,
  groups: props.groups.filter((group) => (group.areaKey ?? 'general') === key),
})).filter((area) => area.groups.length))

function prerequisiteNames(permission) {
  const names = permission.prerequisiteKeys.map((key) => permissionName(t, { key, displayName: key }))
  return t('requiresPermissions', { keys: names.join(', ') })
}

function selectedCount(group) {
  return group.permissions.filter((permission) => props.selected.includes(permission.key) || permission.lockedBase).length
}

function groupPanelId(group) {
  return `permission-group-${group.key.replace(/[^a-zA-Z0-9_-]/g, '-')}`
}

function isExpanded(group) {
  return expandedGroups.value.has(group.key)
}

function toggleGroup(group) {
  const next = new Set(expandedGroups.value)
  if (next.has(group.key)) next.delete(group.key)
  else next.add(group.key)
  expandedGroups.value = next
}

function toggle(permission) {
  if (props.readonly || permission.lockedBase || !permission.customGrantable) return
  const next = new Set(props.selected)
  if (next.has(permission.key)) next.delete(permission.key)
  else next.add(permission.key)
  emit('update:selected', [...next])
}
</script>

<template>
  <div class="permission-matrix">
    <div v-for="area in areas" :key="area.key" class="permission-area">
      <h4>{{ permissionAreaName(t, area.key) }}</h4>
      <section v-for="group in area.groups" :key="group.key" :class="{ expanded: isExpanded(group) }">
      <header>
        <button class="permission-group-toggle" type="button" :aria-expanded="isExpanded(group)"
          :aria-controls="groupPanelId(group)" @click="toggleGroup(group)">
          <span class="permission-group-name"><span class="permission-group-chevron" aria-hidden="true">›</span>{{ permissionGroupName(t, group) }}</span>
          <span class="permission-group-count">{{ selectedCount(group) }} / {{ group.permissions.length }}</span>
        </button>
      </header>
      <div v-show="isExpanded(group)" :id="groupPanelId(group)" class="permission-group-items">
        <label v-for="permission in group.permissions" :key="permission.key"
          :class="{ locked: permission.lockedBase || !permission.customGrantable, risky: permission.risk === 'high' }">
          <input type="checkbox" :checked="selected.includes(permission.key) || permission.lockedBase"
            :disabled="readonly || permission.lockedBase || !permission.customGrantable" @change="toggle(permission)" />
          <span><strong>{{ permissionName(t, permission) }}</strong><small>{{ permissionName(t, permission) }}<template v-if="permission.prerequisiteKeys.length"> · {{ prerequisiteNames(permission) }}</template></small></span>
          <em v-if="permission.lockedBase">{{ t('requiredPermission') }}</em>
          <em v-else-if="!permission.customGrantable">{{ t('administratorOnly') }}</em>
          <em v-else-if="permission.risk === 'high'">{{ t('highRisk') }}</em>
        </label>
      </div>
      </section>
    </div>
  </div>
</template>
